import { ColorValue } from "@/styles/nawy-theme/foundation/colors/semantic";
import {
  Box,
  Button,
  Chip,
  Collapse,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  LucideIcon,
  Sparkles,
} from "lucide-react";
import { ReactNode, useMemo, useState } from "react";
import {
  ParsedLeadMining,
  splitExtras,
} from "@/modules/lead-page/new/components/activity/lead-mining/parser";

const CARD_RADIUS = "12px";
const SUMMARY_PREVIEW = 3;

type IntentVariant = "inline" | "stacked";

const Card = ({
  children,
  background,
  padding,
}: {
  children: ReactNode;
  background?: ColorValue | string;
  padding?: string;
}) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        padding: padding ?? "12px",
        borderRadius: CARD_RADIUS,
        backgroundColor:
          background ?? theme.nawy.colors.primitives.white[200],
      }}
    >
      {children}
    </Box>
  );
};

const CardLabel = ({
  text,
  Icon,
  iconColor,
}: {
  text: string;
  Icon?: LucideIcon;
  iconColor?: ColorValue;
}) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "flex",
        gap: "4px",
        height: 20,
        alignItems: "center",
      }}
    >
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 400,
          lineHeight: "16px",
          color: theme.nawy.colors.semantic.text.tertiary,
          whiteSpace: "nowrap",
        }}
      >
        {text}
      </Typography>
      {Icon && <Icon size={16} color={iconColor} />}
    </Box>
  );
};

const Banner = ({
  Icon,
  iconColor,
  background,
  body,
  bodyWeight = "regular",
}: {
  Icon: LucideIcon;
  iconColor: ColorValue;
  background: ColorValue | string;
  body: string;
  bodyWeight?: "regular" | "semibold";
}) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "flex",
        gap: "8px",
        alignItems: "flex-start",
        p: "12px",
        borderRadius: CARD_RADIUS,
        backgroundColor: background,
      }}
    >
      <Icon
        size={16}
        color={iconColor}
        style={{ flexShrink: 0, marginTop: 2 }}
      />
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: bodyWeight === "semibold" ? 600 : 400,
          lineHeight: "18px",
          color: theme.nawy.colors.semantic.text.primary,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {body}
      </Typography>
    </Box>
  );
};

const splitSummary = (text: string): string[] => {
  const lines = text
    .split(/\n+/)
    .map((l) => l.trim().replace(/^[-•]\s*/, ""))
    .filter(Boolean);
  if (lines.length > 1) return lines;
  const sentences = text
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
  return sentences.length > 0 ? sentences : [text];
};

interface LeadMiningSectionsProps {
  data: ParsedLeadMining;
  onViewDetails?: () => void;
  intentVariant?: IntentVariant;
  showJourneyNarrative?: boolean;
  /**
   * Controls visibility of escalation alert and follow-up gaps.
   * Defaults to true (drawer); inline card passes false.
   */
  showExtras?: boolean;
}

const LeadMiningSections = ({
  data,
  onViewDetails,
  intentVariant = "inline",
  showJourneyNarrative = true,
  showExtras = true,
}: LeadMiningSectionsProps) => {
  const theme = useTheme();
  const { escalation, followUpGaps } = splitExtras(data);

  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const bulletItems = data.signals ?? [];
  const isStacked = intentVariant === "stacked";
  const showAllBullets = isStacked || summaryExpanded;

  return (
    <Stack direction="column" gap={theme.nawy.spacing.xs} width="100%">
      {intentVariant === "stacked" && data.intent && data.intent.length > 0 && (
        <Box
          sx={{
            p: "12px",
            borderRadius: CARD_RADIUS,
            backgroundColor: theme.nawy.colors.primitives.white[200],
            display: "flex",
            flexDirection: "column",
            gap: "10px",
          }}
        >
          <CardLabel text="Client intent" />
          <Stack direction="column" gap="8px">
            {data.intent.map(({ label, value }, idx) => (
              <Box
                key={`${label}-${idx}`}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "12px",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 400,
                    lineHeight: "18px",
                    color: theme.nawy.colors.semantic.text.tertiary,
                    minWidth: 120,
                    flexShrink: 0,
                  }}
                >
                  {label}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    lineHeight: "18px",
                    color: theme.nawy.colors.semantic.text.primary,
                    wordBreak: "break-word",
                    flex: 1,
                  }}
                >
                  {value}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      <Card>
        <CardLabel
          text="Re-engagement brief"
          Icon={Sparkles}
          iconColor={theme.nawy.colors.semantic.indicator.green}
        />

        {intentVariant === "inline" && data.intent && data.intent.length > 0 && (
          <Box
            sx={{
              display: "flex",
              gap: "4px",
              flexWrap: "wrap",
              minWidth: 0,
              maxWidth: "100%",
            }}
          >
            {data.intent.map(({ label, value }, idx) => (
              <Chip
                key={`${label}-${idx}`}
                label={`${label}: ${value}`}
                size="small"
                sx={{
                  fontSize: 12,
                  fontWeight: 400,
                  lineHeight: "18px",
                  color: theme.nawy.colors.semantic.text.primary,
                  backgroundColor: theme.nawy.colors.semantic.layer.primary,
                  borderRadius: "4px",
                  height: "auto",
                  maxWidth: "100%",
                  "& .MuiChip-label": {
                    px: theme.nawy.spacing.xs,
                    py: "2px",
                    whiteSpace: "normal",
                    overflowWrap: "anywhere",
                  },
                }}
              />
            ))}
          </Box>
        )}

        {data.behaviorSummary && (
          <Typography
            sx={{
              ...theme.nawy.typography.english.variants.body.md.regular,
              color: theme.nawy.colors.semantic.text.primary,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {data.behaviorSummary}
          </Typography>
        )}

        {bulletItems.length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
            }}
          >
            <Box
              component="ul"
              sx={{
                m: 0,
                pl: "20px",
                display: "flex",
                flexDirection: "column",
                gap: "4px",
              }}
            >
              {(showAllBullets
                ? bulletItems
                : bulletItems.slice(0, SUMMARY_PREVIEW)
              ).map((point, idx) => (
                <Typography
                  key={idx}
                  component="li"
                  sx={{
                    ...theme.nawy.typography.english.variants.body.md.regular,
                    color: theme.nawy.colors.semantic.text.primary,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {point}
                </Typography>
              ))}
            </Box>
            {!isStacked && bulletItems.length > SUMMARY_PREVIEW && (
              <Button
                onClick={() => setSummaryExpanded((prev) => !prev)}
                sx={{
                  mt: 0.5,
                  p: 0,
                  fontSize: "0.75rem",
                  textTransform: "none",
                  color: theme.nawy.colors.semantic.text.accent,
                  minWidth: "auto",
                  "&:hover": { background: "transparent" },
                }}
              >
                {summaryExpanded
                  ? "View less"
                  : `View more (${bulletItems.length - SUMMARY_PREVIEW} more)`}
              </Button>
            )}
          </Box>
        )}
      </Card>

      {data.salesRecommendation && (
        <Banner
          Icon={CheckCircle2}
          iconColor={theme.nawy.colors.semantic.indicator.green}
          background={theme.nawy.colors.semantic.indicator.greenLight}
          body={data.salesRecommendation}
        />
      )}

      {showJourneyNarrative && data.journeyNarrative && (
        <JourneyNarrativeCard text={data.journeyNarrative} />
      )}

      {showExtras && escalation && (
        <Banner
          Icon={AlertCircle}
          iconColor={theme.nawy.colors.semantic.indicator.red}
          background={theme.nawy.colors.semantic.indicator.redLight}
          body={escalation.value}
          bodyWeight="semibold"
        />
      )}

      {showExtras && followUpGaps.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            p: "12px",
            borderRadius: CARD_RADIUS,
            backgroundColor: theme.nawy.colors.semantic.indicator.yellowLight,
          }}
        >
          {followUpGaps.map(({ label, value }, idx) => (
            <Box
              key={`${label}-${idx}`}
              sx={{ display: "flex", alignItems: "flex-start", gap: "8px" }}
            >
              <AlertCircle
                size={16}
                color={theme.nawy.colors.semantic.indicator.yellowDark}
                style={{ flexShrink: 0, marginTop: 2 }}
              />
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 400,
                  lineHeight: "18px",
                  color: theme.nawy.colors.semantic.text.primary,
                }}
              >
                {label}: {value}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {onViewDetails && (
        <Button
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onViewDetails();
          }}
          sx={{
            alignSelf: "flex-start",
            textTransform: "none",
            ...theme.nawy.typography.english.variants.body.sm.semibold,
            color: theme.nawy.colors.semantic.text.accent,
            px: 0,
            minWidth: "auto",
            "&:hover": {
              background: "transparent",
              textDecoration: "underline",
            },
          }}
        >
          {"View full re-engagement →"}
        </Button>
      )}
    </Stack>
  );
};

const JourneyNarrativeCard = ({ text }: { text: string }) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const points = useMemo(() => splitSummary(text), [text]);

  return (
    <Box
      sx={{
        p: "12px",
        borderRadius: CARD_RADIUS,
        backgroundColor: theme.nawy.colors.primitives.white[200],
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        onClick={() => setOpen((p) => !p)}
        sx={{ cursor: "pointer", userSelect: "none" }}
      >
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 400,
            lineHeight: "16px",
            color: theme.nawy.colors.semantic.text.tertiary,
          }}
        >
          Journey narrative
        </Typography>
        {open ? (
          <ChevronDown
            size={18}
            color={theme.nawy.colors.semantic.text.tertiary}
          />
        ) : (
          <ChevronRight
            size={18}
            color={theme.nawy.colors.semantic.text.tertiary}
          />
        )}
      </Stack>
      <Collapse in={open}>
        <Box
          component="ul"
          sx={{
            m: 0,
            mt: theme.nawy.spacing.xs,
            pl: "20px",
            display: "flex",
            flexDirection: "column",
            gap: "4px",
          }}
        >
          {points.map((point, idx) => (
            <Typography
              key={idx}
              component="li"
              sx={{
                ...theme.nawy.typography.english.variants.body.md.regular,
                color: theme.nawy.colors.semantic.text.primary,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {point}
            </Typography>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

export default LeadMiningSections;
